import { Module } from 'vuex';
import Vue from 'vue';
import { RootState } from '@/store';
import axios from 'axios';
import { markModule } from './StoreTypes';
import { directivesPlugin } from 'bootstrap-vue';

export interface ILookupType {
    recID: number;
    code: string;
    name: string | null;
    description: string | null;
    order: number | null;
    parentCode: string | null;
}

// These must stay in sync with names in the server code for the matching to occur
export enum LocationLookupType {
    ViolationCode,
    Officer,
    Location,
    State,
    HandheldComment,
    VehicleColor,
    VehicleMake,
    Block,
    NoteType,
    PermitType,
    PlateType,
    VehicleType,
    PaymentMethod,
    CustomerFileType,
    ViolationBehaviorProfile,
    RevenueType,
    Tax,
    HandheldTicketTemplate,
    EscalationGroup,
    TicketComments,
    PermitCategory
}

export enum GeneralLookupType {
    PlateType
}

export interface LookupsState {
    locationLookups: {
        [location: string]: {
            [type in keyof typeof LocationLookupType]: { [code: string]: ILookupType };
        },
    };
    generalLookups: {
        [type in keyof typeof GeneralLookupType]: { [code: string]: ILookupType };
    };
}

type lookupPromiseDictionary<T> = { [type in keyof T]: Promise<ILookupType[]> };
type locationPromises = lookupPromiseDictionary<typeof LocationLookupType>;
type generalPromises = lookupPromiseDictionary<typeof GeneralLookupType>;
interface LookupRequestCache {
    locations: {
        [location: string]: locationPromises;
    };
    generalRequests: generalPromises;
}

const cachedRequests: LookupRequestCache = {
    locations: {},
    generalRequests: {} as generalPromises,
};

interface LocationLookupPayload {
    location: string;
    lookupType: LocationLookupType;
}

interface GeneralLookupPayload {
    lookupType: GeneralLookupType;
}

export const lookupsModule = markModule({
    namespaced: true,
    state: {
        locationLookups: {},
        generalLookups: {},
    } as LookupsState,
    getters: {},
    mutations: {
        setLocationLookups(state, params: { type: LocationLookupType, location: string, lookups: ILookupType[] }) {
            const { type, location, lookups } = params;
            if (!state.locationLookups[location]) {
                Vue.set(state.locationLookups, location, {});
            }

            const dictionary = lookups.reduce((dict, item) => {
                dict[item.code] = item;
                return dict;
            }, {} as { [code: string]: ILookupType });
            Vue.set(state.locationLookups[location], type, dictionary);
        },
        setGeneralLookups(state, params: { type: GeneralLookupType, lookups: ILookupType[] }) {
            const { type, lookups } = params;

            const dictionary = lookups.reduce((dict, item) => {
                // keyed on recid because codes sometimes have collisions
                dict[item.recID] = item;
                return dict;
            }, {} as {[recID: string]: ILookupType});

            Vue.set(state.generalLookups, type, dictionary);
        }
    },
    actions: {
        async reloadLocationLookups({ state, dispatch }, { lookupType, location }: LocationLookupPayload):
                Promise<{ [code: string]: ILookupType}> {
            if (cachedRequests.locations[location] && cachedRequests.locations[location][lookupType]) {
                delete cachedRequests.locations[location][lookupType];
            }

            if (state.locationLookups[location] && state.locationLookups[location][lookupType]) {
                delete state.locationLookups[location][lookupType];
            }

            return await dispatch('getLocationLookups', { lookupType, location });
        },
        async getLocationLookups({ commit, state }, { lookupType, location }: LocationLookupPayload):
            Promise<{ [code: string]: ILookupType}> {
            if (!state.locationLookups[location]
                || !state.locationLookups[location][lookupType]
                || !state.locationLookups[location][lookupType].length) {

                if (!cachedRequests.locations[location]) {
                    cachedRequests.locations[location] = {} as locationPromises;
                }

                if (!cachedRequests.locations[location][lookupType]) {
                    const request = axios.get<ILookupType[]>(`/api/${location}/Lookups/${lookupType}`)
                        .then(resp => resp.data);
                    cachedRequests.locations[location][lookupType] = request;
                    const data = await request;
                    commit('setLocationLookups', { type: lookupType, location, lookups: data });
                }
                else {
                    await cachedRequests.locations[location][lookupType];
                }
            }
            return state.locationLookups[location][lookupType];
        },
        async getGeneralLookups({commit, state}, { lookupType }: GeneralLookupPayload):
            Promise<{ [recID: string]: ILookupType}> {
                if (!state.generalLookups
                    || !state.generalLookups[lookupType]
                    || !state.generalLookups[lookupType].length) {
                    if (!cachedRequests.generalRequests[lookupType]) {
                        const request = axios.get<ILookupType[]>(`/api/Lookups/${lookupType}`)
                            .then(resp => resp.data);
                        cachedRequests.generalRequests[lookupType] = request;
                        const data = await request;
                        commit('setGeneralLookups', { type: lookupType, lookups: data });
                    }
                    else {
                        await cachedRequests.generalRequests[lookupType];
                    }
                }

                return state.generalLookups[lookupType];
            }
    },
    modules: {}
}, {} as RootState);
